/**
 * Application shell: session → route → view, plus the ambient bed and the effects overlay.
 *
 * The gate is enforced here as well as on the server: an unauthenticated visitor can only see the
 * landing page, and an authenticated-but-unconsented one can only see the consent gate.
 */
import { api, ApiError } from './api/client.js';
import type { LegalDocumentPayload, MeResponse } from './api/types.js';
import { t } from './i18n/index.js';
import { ProgressSync } from './state/progress.js';
import { startAmbientBed, type AmbientBed } from './audio/ambient.js';
import { Mixer } from './audio/mixer.js';
import {
  UNKNOWN_POPULATION,
  applyTasks,
  holdEngagedOnAir,
  signalEligibility,
} from './world/npc.js';
import { emptyState } from './story/gates.js';
import { Receiver } from './audio/receiver.js';
import { WorldClock, type WorldTime } from './world/clock.js';
import type { ScheduledSignal } from './world/matrix.js';
import { fetchPack, type LoadedPack } from './content/loader.js';
import { Investigation } from './story/investigation.js';
import { GhostPlayer } from './audio/ghostVoices.js';
import { SpeechPlayer } from './audio/speech.js';
import { GhostRelay } from './ghosts/relay.js';

/** The ghost stack, as the radio view needs it. */
export interface Ghosts {
  player: GhostPlayer;
  relay: GhostRelay;
  /** Called whenever a fragment arrives. Returns an unsubscribe. */
  onArrival: (listener: () => void) => () => void;
}
import { locale } from './i18n/index.js';
import { unlockOnGesture } from './audio/context.js';
import { mountOverlay, refreshOverlay } from './ui/effects/overlay.js';
import { h } from './ui/dom.js';
import { navigate, startRouter, type RouteName } from './ui/router.js';
import type { View } from './ui/view.js';
import { consentView } from './ui/views/consent.js';
import { landingView } from './ui/views/landing.js';
import { menuView } from './ui/views/menu.js';
import { radioView } from './ui/views/radio.js';
import { banner, footer } from './ui/components/chrome.js';

/** The ambient bed's own output level, before the mixer's ambient-bus trim. */
const AMBIENT_SOURCE_LEVEL = 0.35;
/** Background music lives only on the landing page; every other screen mutes the ambient bus. */
const AMBIENT_BUS_LANDING = 1;
/** How often elapsed world time is folded into the save. */
const CLOCK_PERSIST_MS = 20_000;

/**
 * The content pack the world is built from. Everything about what is on the air — and, from
 * Chapter 3, the whole mystery — lives in `client/public/content/<id>/pack.json`. Changing that
 * file changes the game with no code edit; `ch2-sample` is still shipped and still loads.
 */
const PACK_ID = 'ch3-mystery';

export class App {
  private root: HTMLElement;
  private current: View | null = null;
  private me: MeResponse | null = null;
  private sync: ProgressSync | null = null;
  private ambient: AmbientBed | null = null;
  private mixer: Mixer | null = null;
  private receiver: Receiver | null = null;
  private clock: WorldClock | null = null;
  private pack: LoadedPack | null = null;
  private investigation: Investigation | null = null;
  private ghosts: Ghosts | null = null;
  private speech: SpeechPlayer | null = null;
  private legalDocuments: LegalDocumentPayload[] | null = null;
  private signInError: string | null = null;
  private clockPersistTimer: ReturnType<typeof setInterval> | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  async start(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    this.signInError = params.get('error');
    if (this.signInError) {
      // Keep the URL clean once the message has been captured.
      window.history.replaceState({}, '', window.location.pathname);
    }

    mountOverlay();
    unlockOnGesture();

    try {
      this.me = await api.me();
    } catch {
      this.renderFatal(t('error.offline'));
      return;
    }

    this.applyEffectsPreference();

    // One mixer for the whole session: the entry ambience and the receiver share its buses and
    // its master limiter, so moving between screens is a level change, not a graph rebuild.
    this.mixer = new Mixer();
    void this.mixer.start();
    this.ambient = startAmbientBed(AMBIENT_SOURCE_LEVEL, this.mixer.bus('ambient'));

    startRouter((route) => void this.render(route));

    // Closing the tab is the common way to stop playing, and it is not a route change: fold the
    // world clock in before the last sync flush, or the seconds since the periodic write are
    // lost and the night quietly rewinds a little on the next visit.
    window.addEventListener('beforeunload', () => {
      this.persistClock();
      void this.sync?.flush();
    });
  }

  /** Route guard: where is this visitor actually allowed to be right now? */
  private resolveRoute(requested: RouteName): RouteName {
    const me = this.me;
    if (!me?.authenticated) return 'landing';
    if (!me.canPlay) return requested === 'landing' ? 'landing' : 'consent';
    // A consented player never lands back on the consent gate — the Main Menu is the hub they
    // enter from, so a stray /consent (e.g. straight after accepting) resolves to it.
    return requested === 'consent' ? 'menu' : requested;
  }

  private async render(requested: RouteName): Promise<void> {
    const route = this.resolveRoute(requested);
    if (route !== requested) {
      navigate(route, true);
      return;
    }

    this.current?.dispose?.();

    // Leaving the receiver puts the world on hold: nothing is scheduled past a player who is
    // not listening.
    if (route !== 'play' && this.clock?.isRunning) {
      this.clock.stop();
      this.stopClockPersistence();
      this.persistClock();
    }

    // Background music belongs to the landing page and the Main Menu (Deshret's spec: the menu
    // shares the landing bed). The consent gate and the receiver run dry, so the game itself
    // carries no bed.
    const bedScreen = route === 'landing' || route === 'menu';
    this.mixer?.setBusLevel('ambient', bedScreen ? AMBIENT_BUS_LANDING : 0, 0.6);

    const view = await this.buildView(route);
    this.current = view;
    this.root.replaceChildren(view.element);
    refreshOverlay();
    document.title = route === 'landing' ? t('app.name') : `${t('app.name')} — ${titleFor(route)}`;
  }

  private async buildView(route: RouteName): Promise<View> {
    switch (route) {
      case 'consent': {
        if (!this.legalDocuments) {
          try {
            this.legalDocuments = await api.legalDocuments();
          } catch {
            return { element: fatalElement(t('error.offline')) };
          }
        }
        return consentView({
          documents: this.legalDocuments,
          onAccepted: () => void this.refreshSessionAndGo('menu'),
          onDeclined: () => void this.signOut(),
        });
      }

      case 'menu': {
        const sync = await this.ensureSync();
        this.mixer?.setVolume(sync.progress.get().settings.masterVolume);
        return menuView({
          me: this.me!,
          sync,
          mixer: this.mixer,
          onContinue: () => navigate('play'),
          onNewGame: () => void this.startNewGame(),
          onLoadSlot: (slot) => this.loadSlotIntoGame(slot),
          onLogOut: () => void this.signOut(),
        });
      }

      case 'play': {
        const sync = await this.ensureSync();
        // Stamp arrival so "Continue" and the save slots show a meaningful last-played time even
        // for a player who enters and reads rather than touching the dial.
        sync.patch((current) => ({ ...current, lastPlayedAt: new Date().toISOString() }));
        const saved = sync.progress.get();
        const mixer = this.mixer!;
        mixer.setVolume(saved.settings.masterVolume);

        // The world is data. If the pack fails to load the receiver still works — the band is
        // simply empty — rather than the whole screen failing.
        const pack = await this.ensurePack();

        // The world runs while the player is on this screen, and only while they are. Its
        // elapsed total rides along with the rest of the save.
        const clock =
          this.clock ??
          new WorldClock({
            elapsedGameMs: saved.world.elapsedGameMs,
            ratio: pack?.clock.ratio,
            startGameMs: pack?.clock.startGameMs,
          });
        this.clock = clock;
        clock.start();
        this.startClockPersistence(sync, clock);

        const receiver = this.receiver ?? new Receiver({ mixer, frequencyKhz: saved.frequencyKhz });
        this.receiver = receiver;
        // The story layer, when the pack carries one. It owns clues, events and dialogue
        // position, and writes them through the same sync the dial uses.
        const investigation =
          this.investigation ?? (pack?.story ? new Investigation(pack.story, sync) : null);
        this.investigation = investigation;
        if (investigation) {
          // Progression reaches the queryable columns with every save.
          sync.summaryProvider = () => investigation.summary();
        }

        // One question, asked of the world at the moment it is asked: what is receivable here,
        // now? The receiver knows nothing about schedules, drift or clues.
        //
        // The story gate is passed *into* the matrix rather than applied to its answer: a locked
        // signal must not take part in the one-transmitter-per-band contest at all, or it would
        // mask an unlocked lower-priority signal sharing its frequency and the player would hear
        // dead air where something is actually broadcasting.
        // NPC tasks are resolved here rather than baked into the matrix, because a task depends
        // on the clock *and* on what the player has worked out — neither of which the matrix
        // knows about. Built once; it reads the state fresh on every call.
        const npcTasks = pack
          ? applyTasks(pack.npcs, () => investigation?.view.get().state ?? emptyState())
          : undefined;
        // ...and an NPC mid-conversation stays keyed up, or a two-minutes-in-seven duty cycle
        // would cut the player off before they had finished reading the line.
        const npcEngaged = holdEngagedOnAir(
          (signalId) => investigation?.view.get().dialogue?.signalId === signalId,
        );
        const npcAdapter = npcTasks
          ? (signal: ScheduledSignal, time: WorldTime) => npcEngaged(npcTasks(signal, time))
          : undefined;

        receiver.setResolver((frequencyKhz) => {
          if (!pack) return null;
          return pack.matrix.resolveAtFrequency(
            frequencyKhz,
            clock.time(),
            signalEligibility({
              // Read per call, not captured: the band fills and empties while the radio is on.
              population: this.ghosts?.relay.population() ?? UNKNOWN_POPULATION,
              gateOpen: (requires) =>
                investigation ? investigation.isGateOpen(requires) : requires === undefined,
            }),
            npcAdapter,
          );
        });

        // Ghosts are enrichment. Everything below is allowed to fail quietly: with no relay, the
        // band is empty, which is exactly how it sounds when nobody else is online.
        const ghosts = this.ghosts ?? this.startGhosts(mixer, saved.settings.muteGhosts);
        this.ghosts = ghosts;

        // Authored NPC voice. Nothing depends on it: a pack with no recordings plays as text.
        const speech = (this.speech ??= new SpeechPlayer({ mixer }));
        ghosts?.relay.tune(saved.frequencyKhz);

        return radioView({
          me: this.me!,
          sync,
          receiver,
          clock,
          pack,
          investigation,
          ghosts,
          speech,
          onAbandon: () => void this.abandonToMenu(),
          onLoadSlot: (slot) => this.loadSlotIntoGame(slot),
        });
      }

      case 'landing':
      default:
        return landingView({ me: this.me!, error: this.signInError });
    }
  }

  /**
   * Load the content pack once per session. A failure is logged and survived: the player gets a
   * working receiver on a dead band instead of an error screen, which is also exactly what
   * Chapter 1 shipped.
   */
  /**
   * Open the relay and start listening for other players.
   *
   * Returns null if anything at all goes wrong. There is deliberately no error path to the
   * player: a relay that cannot be reached is indistinguishable from a band with nobody on it,
   * and that is the correct experience rather than a degraded one.
   */
  private startGhosts(mixer: Mixer, muted: boolean): Ghosts | null {
    try {
      const player = new GhostPlayer({ mixer });
      player.setMuted(muted);

      // A ghost is audible and leaves no other trace — no log entry, no clue, nothing on screen.
      // For a player using captions it would otherwise simply not exist, so arrivals are
      // broadcast to whoever is listening rather than being swallowed by the player.
      const arrivals = new Set<() => void>();

      const relay = new GhostRelay({
        onGhost: (ghost) => {
          void player.play(ghost.audio);
          for (const listener of arrivals) listener();
        },
      });
      relay.connect();

      return {
        player,
        relay,
        onArrival: (listener) => {
          arrivals.add(listener);
          return () => arrivals.delete(listener);
        },
      };
    } catch {
      return null;
    }
  }

  private async ensurePack(): Promise<LoadedPack | null> {
    if (this.pack) return this.pack;
    try {
      const pack = await fetchPack(PACK_ID, locale());
      for (const warning of pack.warnings) {
        console.warn(`[nightband] pack ${pack.id}: ${warning.path} — ${warning.message}`);
      }
      this.pack = pack;
      return pack;
    } catch (error) {
      console.error('[nightband] content pack failed to load', error);
      return null;
    }
  }

  /**
   * Load a manual save slot into the live game and enter it.
   *
   * The slot blob becomes the active autosave (`sync.adopt`), then the cached game objects are
   * dropped so `/play` rebuilds the receiver, world clock and investigation from the loaded save
   * rather than from wherever the player happened to be.
   */
  private async loadSlotIntoGame(slot: number): Promise<void> {
    const sync = await this.ensureSync();
    const record = await api.loadSlot(slot);
    await sync.adopt(record.data);
    this.applyEffectsPreference(sync.progress.get().settings.effects);
    this.resetGameForLoad();
    navigate('play');
  }

  /**
   * Start a fresh night. Discards the live autosave (reset to a clean `defaultProgress()`), drops
   * the cached game objects so `/play` rebuilds from the reset save, and enters the receiver.
   */
  private async startNewGame(): Promise<void> {
    const sync = await this.ensureSync();
    await sync.reset();
    this.applyEffectsPreference(sync.progress.get().settings.effects);
    this.resetGameForLoad();
    navigate('play');
  }

  /**
   * Abandon the current game. Same reset as New Game — the live autosave is discarded, so a later
   * "Continue" starts fresh — but returns to the Main Menu rather than straight back into play.
   */
  private async abandonToMenu(): Promise<void> {
    const sync = await this.ensureSync();
    await sync.reset();
    this.applyEffectsPreference(sync.progress.get().settings.effects);
    this.resetGameForLoad();
    navigate('menu');
  }

  /** Drop the cached game objects so the next `/play` build starts from the current save. */
  private resetGameForLoad(): void {
    this.stopClockPersistence();
    this.investigation?.dispose();
    this.investigation = null;
    this.clock?.stop();
    this.clock = null;
    this.receiver?.dispose();
    this.receiver = null;
    // The ghost relay is network presence, independent of the save — it keeps running. Speech is
    // stateless between lines. Both are safely reused by the rebuilt play view.
  }

  private async ensureSync(): Promise<ProgressSync> {
    if (this.sync) return this.sync;
    const sync = new ProgressSync(this.me!.account!.id);
    this.sync = sync;
    await sync.load();
    this.applyEffectsPreference(sync.progress.get().settings.effects);
    return sync;
  }

  private applyEffectsPreference(preference?: 'full' | 'reduced' | 'off'): void {
    document.documentElement.dataset.nbEffects = preference ?? 'full';
    refreshOverlay();
  }

  /**
   * Fold elapsed game time into the save periodically. The clock is authoritative in memory; this
   * just makes sure a crashed tab loses seconds of world time, not a whole session of it.
   */
  private startClockPersistence(sync: ProgressSync, clock: WorldClock): void {
    this.stopClockPersistence();
    this.clockPersistTimer = setInterval(() => {
      // Skip sub-second drift so an idle receiver doesn't generate a write every tick.
      if (
        Math.abs(Math.round(clock.elapsedGameMs()) - sync.progress.get().world.elapsedGameMs) < 1000
      ) {
        return;
      }
      this.persistClock();
    }, CLOCK_PERSIST_MS);
  }

  /** Write the world's elapsed time into the save. Cheap, and safe to call at any moment. */
  private persistClock(): void {
    const clock = this.clock;
    const sync = this.sync;
    if (!clock || !sync) return;
    const elapsedGameMs = Math.round(clock.elapsedGameMs());
    if (elapsedGameMs === sync.progress.get().world.elapsedGameMs) return;
    sync.patch((current) => ({ ...current, world: { ...current.world, elapsedGameMs } }));
  }

  private stopClockPersistence(): void {
    if (this.clockPersistTimer === null) return;
    clearInterval(this.clockPersistTimer);
    this.clockPersistTimer = null;
  }

  private async refreshSessionAndGo(route: RouteName): Promise<void> {
    try {
      this.me = await api.me();
    } catch (err) {
      if (err instanceof ApiError) this.signInError = err.code;
    }
    navigate(route);
  }

  private async signOut(): Promise<void> {
    await this.sync?.flush();
    try {
      await api.logout();
    } catch {
      /* a failed logout still clears local state below */
    }
    this.stopClockPersistence();
    this.ghosts?.relay.close();
    this.ghosts = null;
    this.investigation?.dispose();
    this.investigation = null;
    this.clock?.stop();
    this.clock = null;
    this.receiver?.dispose();
    this.receiver = null;
    this.sync = null;
    this.me = await api.me().catch(() => ({
      authenticated: false,
      googleConfigured: true,
      legal: { eula: { version: '', path: '/terms' }, privacy: { version: '', path: '/privacy' } },
    }));
    navigate('landing');
  }

  private renderFatal(message: string): void {
    this.root.replaceChildren(fatalElement(message));
  }
}

function fatalElement(message: string): HTMLElement {
  return h(
    'div',
    { class: 'nb-page nb-page--error' },
    h(
      'main',
      { class: 'nb-error', id: 'main' },
      h('h1', { class: 'nb-error__title', text: t('error.title') }),
      banner('alert', message),
      h('button', {
        class: 'nb-button',
        type: 'button',
        text: t('error.retry'),
        onclick: () => window.location.reload(),
      }),
    ),
    footer(),
  );
}

function titleFor(route: RouteName): string {
  if (route === 'play') return t('radio.title');
  if (route === 'menu') return t('menu.title');
  return t('consent.title');
}
